// src/components/myteconstruction/OrganizationDashboard/AcceptInvitationPage.js

import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { auth, login } = useContext(AuthContext);

  const token = searchParams.get('token') || '';

  // We'll require the user to set a password if they're brand new
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [userExists, setUserExists] = useState(null);

  // Show/hide password toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invitation token is missing.');
      return;
    }
    axios.get(`/api/myteconstruction/invitations/check?token=${token}`)
      .then((res) => {
        setUserExists(res.data.userExists);
      })
      .catch((err) => {
        console.error(err);
        setError('Invalid invitation or expired link.');
      });
  }, [token]);

  const handleAccept = async () => {
    setError('');
    if (!userExists && password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setStatus('Accepting invitation...');
    try {
      // If userExists === false => pass password
      // If userExists === true => pass empty body
      const payload = userExists ? {} : { password };
      const resp = await axios.post(
        `/api/myteconstruction/invitations/accept?token=${token}`,
        payload
      );

      setStatus(resp.data.message || 'Invitation accepted successfully!');

      // If the user is brand new (userExists === false), auto-login
      if (userExists === false) {
        login({
          token: resp.data.token,
          user_type: resp.data.user_type,
          hasCompletedOnboarding: true,
        });
      }

      // or if userExists === true, we might require them to log in manually
      // or confirm switching org, but in your code you show that flow also.

      navigate('/myteconstruction/dashboard');
    } catch (err) {
      console.error(err);
      setError(err.response?.data || err.message);
      setStatus('');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-gray-800 p-6 rounded-md shadow-md">
          <h2 className="text-xl font-bold text-red-400">Error</h2>
          <p>{String(error)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-gray-800 p-6 rounded-md shadow-md">
        <h1 className="text-2xl font-bold mb-4">Accept Your Invitation</h1>

        {status && <p className="text-blue-300 mb-3">{status}</p>}

        {/* If the invitation is for a brand-new user (userExists===false), show password fields */}
        {userExists === false && !status && (
          <>
            <label className="block mb-2 text-sm">Set Your Password</label>
            <div className="relative mb-4">
              <input
                type={showPassword ? 'text' : 'password'}
                className="w-full p-2 rounded bg-gray-700"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a secure password"
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2"
                type="button"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <label className="block mb-2 text-sm">Confirm Password</label>
            <div className="relative mb-4">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className="w-full p-2 rounded bg-gray-700"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
              />
              <button
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2"
                type="button"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <button
              onClick={handleAccept}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md w-full"
            >
              Create Account & Join
            </button>
          </>
        )}

        {/* If userExists===true and they’re not logged in, we might prompt them to login */}
        {userExists && !auth?.token && !status && (
          <>
            <p className="mb-4">
              You already have an account. Please login to accept the invitation.
            </p>
            <button
              onClick={() => navigate('/myteconstruction/login', { state: { inviteToken: token } })}
              className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md w-full"
            >
              Log In Now
            </button>
          </>
        )}

        {/* If userExists===true and they are logged in, optionally confirm org switch */}
        {userExists && auth?.token && !status && (
          <>
            <p className="mb-3">Looks like you already have an account. Switch organizations?</p>
            <button
              onClick={handleAccept}
              className="bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded-md w-full"
            >
              Confirm Switch
            </button>
          </>
        )}
      </div>
    </div>
  );
}
